/**
 * Classic shell for the familiar modes (theme spec 7.3, S2.1): title strip,
 * menu bar, icon toolbar, optional module bar (left or top), patient strip
 * and status line around the routed page. Presentational only: the app
 * shell feeds it items resolved from the same action, nav and command
 * registries the modern shell uses, so no page is forked.
 *
 * Every visual value comes from tokens (engine.css defaults, overridden per
 * skin). Icons are passed in: Lucide or our own original set, never vendor
 * artwork (spec 7.5).
 */
import { ChevronDown, ChevronsRight } from 'lucide-react';
import { DropdownMenu as RDM, Menubar as RMB } from 'radix-ui';
import {
  Fragment,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
  type MouseEvent as ReactMouseEvent,
  type ReactNode,
} from 'react';
import { cx } from '../lib/cx.js';
import { renderEntries, type MenuEntry } from '../primitives/Menu.js';
import { Badge } from '../primitives/Display.js';
import type { NavLinkRenderProps } from '../shell/AppShell.js';
import '../primitives/overlay.css';
import './engine.css';
import './classic.css';

/* ------------------------------------------------------------ the frame */

export interface ClassicShellProps {
  titleStrip?: ReactNode;
  menuBar?: ReactNode;
  toolbar?: ReactNode;
  moduleBar?: ReactNode;
  moduleBarPlacement?: 'left' | 'top' | 'none';
  patientBar?: ReactNode;
  patientBarPosition?: 'above-toolbar' | 'below-toolbar';
  statusBar?: ReactNode;
  children: ReactNode;
  mainId?: string;
  className?: string;
}

export function ClassicShell({
  titleStrip,
  menuBar,
  toolbar,
  moduleBar,
  moduleBarPlacement = 'none',
  patientBar,
  patientBarPosition = 'below-toolbar',
  statusBar,
  children,
  mainId = 'main',
  className,
}: ClassicShellProps) {
  const placement = moduleBar ? moduleBarPlacement : 'none';
  return (
    <div className={cx('vel-classic', className)} data-modulebar={placement}>
      <a className="vel-skip" href={`#${mainId}`}>
        Skip to content
      </a>
      <div className="vel-classic__chrome">
        {titleStrip}
        {menuBar}
        {patientBarPosition === 'above-toolbar' ? patientBar : null}
        {toolbar}
        {placement === 'top' ? moduleBar : null}
        {patientBarPosition === 'below-toolbar' ? patientBar : null}
      </div>
      <div className="vel-classic__body">
        {placement === 'left' ? moduleBar : null}
        <main id={mainId} className="vel-classic__main" tabIndex={-1}>
          {children}
        </main>
      </div>
      {statusBar}
    </div>
  );
}

/* ------------------------------------------------------------ title strip */

export function TitleStrip({ title, right }: { title: ReactNode; right?: ReactNode }) {
  return (
    <div className="vel-titlestrip">
      <span className="vel-titlestrip__title">{title}</span>
      {right ? <span className="vel-titlestrip__right">{right}</span> : null}
    </div>
  );
}

/* ------------------------------------------------------------- menu bar */

export interface MenuBarMenu {
  id: string;
  /** Put "&" before the access-key letter: "&File" shows F underlined and opens on Alt+F. */
  label: string;
  items: readonly MenuEntry[];
  /** A live count after the label: `Alerts (3)`. */
  count?: number;
  /** Draw the label in the emphasis colour while the count is above 0. */
  emphasize?: boolean;
}

/** Splits "&File" into its access key and the label parts around it. */
export function parseAccessKey(label: string): {
  text: string;
  key: string | null;
  before: string;
  after: string;
} {
  const i = label.indexOf('&');
  if (i < 0 || i === label.length - 1) {
    return { text: label.replace('&', ''), key: null, before: label, after: '' };
  }
  const key = label.charAt(i + 1);
  return {
    text: label.slice(0, i) + label.slice(i + 1),
    key: key.toLowerCase(),
    before: label.slice(0, i),
    after: label.slice(i + 2),
  };
}

export interface MenuBarProps {
  menus: readonly MenuBarMenu[];
  /** Right end of the bar (user name, office, alerts). */
  right?: ReactNode;
  /** Bind Alt+letter to open menus (on by default). */
  accessKeys?: boolean;
}

export function MenuBar({ menus, right, accessKeys = true }: MenuBarProps) {
  const [open, setOpen] = useState('');
  const parsed = menus.map((m) => ({ ...m, access: parseAccessKey(m.label) }));
  const latest = useRef(parsed);
  latest.current = parsed;

  useEffect(() => {
    if (!accessKeys) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (!e.altKey || e.ctrlKey || e.metaKey || e.shiftKey || e.repeat) return;
      const key = e.key.toLowerCase();
      const hit = latest.current.find((m) => m.access.key === key);
      if (!hit) return;
      e.preventDefault();
      setOpen(hit.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [accessKeys]);

  return (
    <div className="vel-menubar">
      <RMB.Root className="vel-menubar__menus" value={open} onValueChange={setOpen}>
        {parsed.map((m) => (
          <RMB.Menu key={m.id} value={m.id}>
            <RMB.Trigger
              className="vel-menubar__trigger"
              data-emphasis={m.emphasize && (m.count ?? 0) > 0 ? '' : undefined}
              {...(m.access.key
                ? { 'aria-keyshortcuts': `Alt+${m.access.key.toUpperCase()}` }
                : {})}
            >
              {m.access.key ? (
                <>
                  {m.access.before}
                  <u>{m.access.text.charAt(m.access.before.length)}</u>
                  {m.access.after}
                </>
              ) : (
                m.access.text
              )}
              {m.count !== undefined ? (
                <span className="vel-menubar__count"> ({m.count})</span>
              ) : null}
            </RMB.Trigger>
            <RMB.Portal>
              <RMB.Content className="vel-float vel-menubar__content" align="start" sideOffset={1}>
                {renderEntries(RMB, m.items)}
              </RMB.Content>
            </RMB.Portal>
          </RMB.Menu>
        ))}
      </RMB.Root>
      {right ? <div className="vel-menubar__right">{right}</div> : null}
    </div>
  );
}

/* ------------------------------------------------------------ toolbar */

export interface ToolbarButton {
  id: string;
  label: string;
  icon: ReactNode;
  onSelect: () => void;
  active?: boolean;
  disabled?: boolean;
  /**
   * Why a disabled button is unavailable: it stays focusable
   * (`aria-disabled`) and the reason is its tooltip, so staff see what is
   * missing instead of a dead button (familiar modes, P1-20).
   */
  disabledReason?: string;
  /** Shown in the tooltip, e.g. "F2". */
  shortcut?: string;
  /** A count badge on the button (hidden at 0). */
  badge?: number;
  /**
   * Show the label beside the icon even on an icon-only toolbar
   * (`labels="none"`): the one button people must always find (the schedule).
   */
  showLabel?: boolean;
}

export type ToolbarItem = ToolbarButton | { id: string; separator: true };

export interface IconToolbarProps {
  items: readonly ToolbarItem[];
  labels?: 'none' | 'below' | 'right';
  'aria-label': string;
  /** Extra content at the right end (search, What's new). */
  right?: ReactNode;
  /** Right-click on the toolbar (Customize Toolbar). */
  onContextMenu?: (e: ReactMouseEvent<HTMLDivElement>) => void;
  /**
   * What happens when the buttons do not fit on one row: `wrap` (default)
   * starts another row; `menu` keeps one row and moves the buttons that do
   * not fit into a "More" drop-down at the end, as the real PMS toolbars
   * do, so the search box and the right end are never pushed off screen.
   */
  overflow?: 'wrap' | 'menu';
}

function isSeparator(item: ToolbarItem): item is { id: string; separator: true } {
  return 'separator' in item;
}

/** Roving focus: arrows move between buttons, Home and End jump (WAI-ARIA toolbar). */
function onToolbarKey(e: ReactKeyboardEvent<HTMLDivElement>) {
  const keys = ['ArrowLeft', 'ArrowRight', 'Home', 'End'];
  if (!keys.includes(e.key)) return;
  const buttons = [...e.currentTarget.querySelectorAll<HTMLButtonElement>('button:not(:disabled)')];
  const i = buttons.indexOf(document.activeElement as HTMLButtonElement);
  const next =
    e.key === 'Home'
      ? 0
      : e.key === 'End'
        ? buttons.length - 1
        : (i + (e.key === 'ArrowRight' ? 1 : -1) + buttons.length) % buttons.length;
  buttons[next]?.focus();
  e.preventDefault();
}

/** Title, disabled state and click handler of one toolbar button. */
function toolbarButtonState(item: ToolbarButton) {
  const plain = item.shortcut ? `${item.label} (${item.shortcut})` : item.label;
  if (item.disabled && item.disabledReason) {
    return {
      'aria-disabled': true as const,
      title: `${item.label}: ${item.disabledReason}`,
      'data-disabled-reason': item.disabledReason,
      onClick: (e: ReactMouseEvent<HTMLButtonElement>) => e.preventDefault(),
    };
  }
  return { title: plain, disabled: item.disabled, onClick: item.onSelect };
}

/** Ids of the toolbar children (buttons and separators) that run past `box`'s right edge. */
function overflowingIds(box: HTMLElement): string[] {
  const limit = box.clientWidth;
  const out: string[] = [];
  for (const el of box.children) {
    const node = el as HTMLElement;
    const id = node.dataset['tbId'];
    if (!id) continue;
    if (node.offsetLeft + node.offsetWidth > limit + 0.5) out.push(id);
  }
  return out;
}

export function IconToolbar({
  items,
  labels = 'none',
  right,
  onContextMenu,
  overflow = 'wrap',
  ...aria
}: IconToolbarProps) {
  const boxRef = useRef<HTMLDivElement>(null);
  const [hidden, setHidden] = useState<readonly string[]>([]);
  const menuMode = overflow === 'menu';
  const itemsKey = items.map((i) => i.id).join('|');

  // One row: every button stays laid out (so its width is known), the ones
  // past the edge are hidden and listed in More. Re-measured on resize.
  useLayoutEffect(() => {
    const box = boxRef.current;
    if (!menuMode || !box) {
      setHidden((h) => (h.length ? [] : h));
      return undefined;
    }
    const measure = () => {
      const next = overflowingIds(box);
      setHidden((prev) =>
        prev.length === next.length && prev.every((id, i) => id === next[i]) ? prev : next,
      );
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(box);
    void document.fonts?.ready.then(measure);
    return () => ro.disconnect();
  }, [menuMode, itemsKey, labels]);

  const hiddenSet = new Set(hidden);
  const overflowButtons = items.filter(
    (i): i is ToolbarButton => !isSeparator(i) && hiddenSet.has(i.id),
  );

  return (
    <div
      className="vel-toolbar"
      role="toolbar"
      aria-label={aria['aria-label']}
      data-labels={labels}
      data-overflow={menuMode ? 'menu' : undefined}
      onKeyDown={onToolbarKey}
      onContextMenu={onContextMenu}
    >
      <div className="vel-toolbar__items" ref={boxRef}>
        {items.map((item) => {
          const off = hiddenSet.has(item.id);
          const offProps = off
            ? { 'data-overflowed': '', 'aria-hidden': true as const, tabIndex: -1 }
            : {};
          return isSeparator(item) ? (
            <span
              key={item.id}
              className="vel-toolbar__sep"
              role="separator"
              data-tb-id={item.id}
              {...(off ? { 'data-overflowed': '' } : {})}
            />
          ) : (
            <button
              key={item.id}
              type="button"
              className="vel-toolbar__btn"
              aria-label={labels === 'none' && !item.showLabel ? item.label : undefined}
              aria-pressed={item.active === undefined ? undefined : item.active}
              {...toolbarButtonState(item)}
              data-action={item.id}
              data-tb-id={item.id}
              data-show-label={item.showLabel ? '' : undefined}
              {...offProps}
            >
              <span className="vel-toolbar__icon" aria-hidden>
                {item.icon}
              </span>
              {labels === 'none' && !item.showLabel ? null : (
                <span className="vel-toolbar__label">{item.label}</span>
              )}
              {item.badge ? (
                <span className="vel-toolbar__badge" aria-label={`${item.badge} new`}>
                  {item.badge > 99 ? '99+' : item.badge}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>
      {overflowButtons.length > 0 ? (
        <RDM.Root>
          <RDM.Trigger asChild>
            <button
              type="button"
              className="vel-toolbar__btn vel-toolbar__more"
              data-toolbar-more=""
              aria-label={`More (${overflowButtons.length})`}
              title={overflowButtons.map((b) => b.label).join(', ')}
            >
              {labels === 'below' ? (
                <>
                  <span className="vel-toolbar__icon" aria-hidden>
                    <ChevronDown />
                  </span>
                  <span className="vel-toolbar__label">More</span>
                </>
              ) : (
                // Icon-only bars: the Windows toolbar overflow chevron.
                <span className="vel-toolbar__icon" aria-hidden>
                  <ChevronsRight />
                </span>
              )}
            </button>
          </RDM.Trigger>
          <RDM.Portal>
            <RDM.Content className="vel-float" align="end" sideOffset={2} collisionPadding={8}>
              {renderEntries(
                RDM,
                overflowButtons.map((b) => ({
                  id: `more-${b.id}`,
                  label: b.label,
                  icon: <span className="vel-toolbar__menuicon">{b.icon}</span>,
                  ...(b.shortcut ? { shortcut: [b.shortcut] } : {}),
                  ...(b.disabled ? { disabled: true, title: b.disabledReason ?? '' } : {}),
                  ...(b.badge ? { badge: b.badge > 99 ? '99+' : b.badge } : {}),
                  onSelect: b.disabled ? () => {} : b.onSelect,
                })),
              )}
            </RDM.Content>
          </RDM.Portal>
        </RDM.Root>
      ) : null}
      {right ? <div className="vel-toolbar__right">{right}</div> : null}
    </div>
  );
}

/* ----------------------------------------------------------- module bar */

export interface ModuleBarItem {
  id: string;
  label: string;
  icon: ReactNode;
  href: string;
  badge?: number;
  locked?: boolean;
}

export interface ModuleBarProps {
  items: readonly ModuleBarItem[];
  /** Velora modules the PMS does not have, after a divider. */
  extras?: readonly ModuleBarItem[];
  activeId?: string;
  orientation: 'vertical' | 'horizontal';
  labels?: boolean;
  renderLink?: (props: NavLinkRenderProps) => ReactNode;
  /** Below the tiles and extras, inside the bar (Open Dental's messaging buttons). Opt-in. */
  footer?: ReactNode;
}

const defaultLink = ({ children, ...props }: NavLinkRenderProps) => <a {...props}>{children}</a>;

export function ModuleBar({
  items,
  extras = [],
  activeId,
  orientation,
  labels = true,
  renderLink = defaultLink,
  footer,
}: ModuleBarProps) {
  const render = (item: ModuleBarItem) => (
    <li key={item.id}>
      {renderLink({
        href: item.href,
        className: 'vel-modulebar__item',
        'aria-current': item.id === activeId ? 'page' : undefined,
        children: (
          <>
            <span className="vel-modulebar__icon" aria-hidden>
              {item.icon}
            </span>
            {labels ? <span className="vel-modulebar__label">{item.label}</span> : null}
            {item.badge ? (
              <Badge tone="neutral" variant="soft" className="vel-modulebar__badge">
                {item.badge > 99 ? '99+' : item.badge}
              </Badge>
            ) : null}
          </>
        ),
      })}
    </li>
  );
  return (
    <nav
      className="vel-modulebar"
      aria-label="Main"
      data-orientation={orientation}
      data-labels={labels ? '' : undefined}
    >
      <ul className="vel-modulebar__list">{items.map(render)}</ul>
      {extras.length > 0 ? (
        <Fragment>
          <div className="vel-modulebar__sep" role="separator" aria-label="More modules" />
          <ul className="vel-modulebar__list vel-modulebar__list--extras">{extras.map(render)}</ul>
        </Fragment>
      ) : null}
      {footer}
    </nav>
  );
}

/* ---------------------------------------------------------- patient strip */

export interface PatientStripProps {
  /** e.g. "Patient:"; the skin's own wording. */
  caption: string;
  /** The current patient's label (masked in privacy mode), or null. */
  patient: ReactNode | null;
  emptyText: string;
  selectLabel: string;
  onSelect: () => void;
  /** Patient-bound shortcuts (Family File, Ledger, Chart...). */
  actions?: ReactNode;
  /** An ID box before the name (`Patient: [ID][Name]`); undefined hides it. */
  patientId?: ReactNode;
  /** `icons`: the actions are 28 px icon buttons. */
  actionStyle?: 'text' | 'icons';
}

export function PatientStrip({
  caption,
  patient,
  emptyText,
  selectLabel,
  onSelect,
  actions,
  patientId,
  actionStyle = 'text',
}: PatientStripProps) {
  return (
    <div
      className="vel-patientstrip"
      role="region"
      aria-label="Current patient"
      data-actions={actionStyle}
    >
      <span className="vel-patientstrip__caption">{caption}</span>
      {patientId !== undefined ? (
        <span className="vel-patientstrip__id" data-phi="">
          {patientId}
        </span>
      ) : null}
      <span className="vel-patientstrip__name" data-phi={patient ? '' : undefined}>
        {patient ?? <span className="vel-patientstrip__empty">{emptyText}</span>}
      </span>
      <button type="button" className="vel-patientstrip__select" onClick={onSelect}>
        {selectLabel}
      </button>
      {actions ? <span className="vel-patientstrip__actions">{actions}</span> : null}
    </div>
  );
}

/* ------------------------------------------------------------ status bar */

export function StatusBar({ children }: { children: ReactNode }) {
  return (
    <footer className="vel-statusbar" role="contentinfo">
      {children}
    </footer>
  );
}
